import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  fetchDocumentById, 
  updateDocument 
} from "@/services/document/documentService";
import { DocumentMetadataEdit } from "@/components/documents/DocumentMetadataEdit";

export default function EditDocument() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("metadata");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch document data
  const { 
    data: document, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ["document", id],
    queryFn: () => fetchDocumentById(id!),
    enabled: !!id
  });

  // Update document mutation
  const updateMutation = useMutation({
    mutationFn: (values: any) => {
      if (!id) throw new Error("Document ID is required");
      return updateDocument(id, values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["document", id] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      toast.success("Document updated successfully");
      navigate(`/documents/${id}`);
    },
    onError: (error: any) => {
      toast.error(`Failed to update document: ${error.message}`);
    }
  });

  // Handle metadata form submission
  const handleMetadataSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      await updateMutation.mutateAsync(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle>Document Not Found</CardTitle>
          <p className="text-sm text-muted-foreground">
            The document you're trying to edit doesn't exist or you don't have permission to access it.
          </p>
        </CardHeader>
        <CardContent>
          <Button variant="secondary" asChild>
            <Link to="/documents">Return to Documents</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/documents/${id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Edit Document</h1>
      </div>

      <Card>
        <CardHeader className="border-b px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full sm:w-[400px] grid-cols-2">
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
              <TabsTrigger value="versions">Versions</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent className="p-6">
          <TabsContent value="metadata">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Document Information</h3>
              <p className="text-sm text-muted-foreground">
                Update metadata for this document, including title, description, and associations.
              </p>

              <DocumentMetadataEdit
                document={document}
                onSave={handleMetadataSubmit}
                isSubmitting={isSubmitting}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="versions">
            <div className="space-y-6">
              <h3 className="text-lg font-medium">Version Management</h3>
              <p className="text-sm text-muted-foreground">
                Version management coming soon. You will be able to upload new versions and manage existing ones.
              </p>

              <Card className="bg-muted/50">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Loader2 className="h-10 w-10 text-muted-foreground mb-4 animate-spin" />
                    <h4 className="text-lg font-medium">Version management is coming soon</h4>
                    <p className="text-sm text-muted-foreground mt-2 max-w-md">
                      In a future update, you'll be able to upload new versions, promote versions, 
                      and manage the version history of your documents.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </CardContent>
      </Card>
    </div>
  );
}